import type { City, Country, Restaurant } from 'domains/cartography';
import { buildCanonicalUrl } from './urls';

type Reference = { '@id': string };
export type OrganizationSchema = Reference & {
  '@type': 'Organization';
  name: string;
  url: string;
};
export type RestaurantSchema = Reference & {
  '@type': 'Restaurant';
  name: string;
  url: string;
  parentOrganization: Reference;
  servesCuisine: string[];
  hasMenu: string;
  address: {
    '@type': 'PostalAddress';
    streetAddress: string;
    addressLocality: string;
    addressCountry?: string;
  };
  telephone?: string;
  image?: string[];
  geo?: { '@type': 'GeoCoordinates'; latitude: number; longitude: number };
};
export type SiteSchema = {
  '@context': 'https://schema.org';
  '@graph': Array<OrganizationSchema | RestaurantSchema>;
};

const text = (value: unknown): string => typeof value === 'string' ? value.trim() : '';

export const buildOrganizationSchema = (origin: string): OrganizationSchema => ({
  '@type': 'Organization',
  '@id': `${buildCanonicalUrl('/', origin)}#organization`,
  name: 'СушиВесла',
  url: buildCanonicalUrl('/', origin),
});

export const buildRestaurantSchema = ({
  restaurant,
  city,
  country,
  telephone,
  origin,
  imageUrls = [],
}: {
  restaurant: Restaurant;
  city: City;
  country: Country | null;
  telephone: string;
  origin: string;
  imageUrls?: string[];
}): RestaurantSchema | null => {
  const address = restaurant?.address;
  const streetAddress = [text(address?.street), text(address?.house)]
    .filter(Boolean).join(', ') || text(address?.address);
  const name = text(restaurant?.name);
  if (!restaurant?.id || restaurant.cityId !== city.id || !streetAddress || !name || !text(city.name)) {
    return null;
  }

  const schema: RestaurantSchema = {
    '@type': 'Restaurant',
    '@id': `${buildCanonicalUrl('/restaurants', origin)}#restaurant-${encodeURIComponent(restaurant.id)}`,
    name,
    url: buildCanonicalUrl('/restaurants', origin),
    parentOrganization: { '@id': buildOrganizationSchema(origin)['@id'] },
    servesCuisine: ['Японская кухня', 'Суши', 'Роллы', 'Вок'],
    hasMenu: buildCanonicalUrl('/', origin),
    address: {
      '@type': 'PostalAddress',
      streetAddress,
      addressLocality: text(city.name),
      ...(country?.id === city.countryId && /^[A-Z]{2}$/.test(country.code?.iso)
        ? { addressCountry: country.code.iso } : {}),
    },
  };
  const phone = text(telephone).replace(/[\s().-]/g, '');
  if (/^\+[1-9]\d{7,14}$/.test(phone)) schema.telephone = phone;

  const { latitude, longitude } = address?.location || {};
  if (
    typeof latitude === 'number' && Number.isFinite(latitude) && Math.abs(latitude) <= 90 &&
    typeof longitude === 'number' && Number.isFinite(longitude) && Math.abs(longitude) <= 180
  ) schema.geo = { '@type': 'GeoCoordinates', latitude, longitude };

  const images = imageUrls.filter((value) => {
    try {
      const url = new URL(value);
      return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password;
    } catch { return false; }
  });
  if (images.length) schema.image = Array.from(new Set(images));

  // The API has no weekly schedule/region. Do not invent those optional fields.
  return schema;
};

export const serializeJsonLd = (value: SiteSchema): string =>
  JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
